import { useEffect, useState } from "react";
import { Zap, Mail, MessageSquare, Check, Minus, Loader2, Clock } from "lucide-react";
import { fetchActivationReport } from "@/lib/api";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

function Sent({ ok, icon: Icon, label }) {
  return (
    <span title={`${label}: ${ok ? "sent" : "not sent"}`} className={`inline-flex items-center gap-0.5 text-[11px] font-semibold rounded-md px-1.5 py-0.5 ${ok ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-400"}`}>
      <Icon size={11} /> {ok ? <Check size={10} strokeWidth={3} /> : <Minus size={10} />}
    </span>
  );
}

export default function ActivationReport({ recId }) {
  const [data, setData] = useState(null);
  const [view, setView] = useState("activated");
  useEffect(() => { if (recId) fetchActivationReport(recId).then(setData).catch(() => setData({ error: true })); }, [recId]);
  if (!data) return <div className="mt-3 text-zinc-400 text-sm"><Loader2 size={14} className="animate-spin inline mr-1" /> Loading activations…</div>;
  if (data.error) return null;
  const s = data.summary;
  const rows = view === "activated" ? data.activated : data.pending;
  return (
    <div className="mt-3 rounded-2xl border border-zinc-200 bg-white p-4" data-testid="activation-report">
      <div className="flex items-center gap-2 font-semibold text-zinc-900"><Zap size={16} className="text-violet-600" /> AI Powerhouse activations</div>
      <div className="text-xs text-zinc-500 mb-3">Who activated, when, and whether the confirmation email / text went out.</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        {[["Activated", s.activated, "bg-emerald-50 text-emerald-800", "activation-kpi-activated"], ["Points awarded", s.points_awarded, "bg-violet-50 text-violet-800", "activation-kpi-points"],
          ["Saw popup, not yet", s.pending, "bg-amber-50 text-amber-800", "activation-kpi-pending"], ["Reminders sent", s.nudged, "bg-zinc-100 text-zinc-700", "activation-kpi-nudged"]].map(([l, v, tone, tid]) => (
          <div key={l} className={`rounded-xl px-3 py-2.5 ${tone}`} data-testid={tid}>
            <div className="text-[10px] uppercase tracking-wide font-semibold opacity-70">{l}</div>
            <div className="text-xl font-bold leading-tight">{v}</div>
          </div>
        ))}
      </div>
      <div className="flex gap-1 mb-2">
        {[["activated", "Activated"], ["pending", "Not activated"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setView(k)} className={`text-xs font-semibold rounded-full px-3 py-1 ${view === k ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`} data-testid={`activation-view-${k}`}>{l}</button>
        ))}
      </div>
      {rows.length === 0 ? <p className="text-sm text-zinc-400 py-4 text-center">Nothing here yet.</p> : (
        <ul className="divide-y divide-zinc-100" data-testid="activation-rows">
          {rows.map((r) => (
            <li key={r.id} className="py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm" data-testid={`activation-row-${r.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-zinc-900 truncate">{r.name}{r.company ? <span className="text-zinc-400 font-normal"> · {r.company}</span> : null}</div>
                <div className="text-xs text-zinc-500 truncate">{r.email}{r.phone ? ` · ${r.phone}` : ""}</div>
              </div>
              {view === "activated" ? (
                <>
                  <span className="text-xs text-zinc-600 inline-flex items-center gap-1"><Clock size={11} /> {fmt(r.activated_at)}</span>
                  <span className="text-xs font-bold text-violet-700">+{r.bonus} pts</span>
                  <Sent ok={r.email_sent} icon={Mail} label="Email" />
                  <Sent ok={r.sms_sent} icon={MessageSquare} label="SMS" />
                </>
              ) : (
                <>
                  <span className="text-xs text-zinc-600 inline-flex items-center gap-1"><Clock size={11} /> saw it {fmt(r.first_seen_at)}</span>
                  <span className={`text-[11px] font-semibold rounded-md px-1.5 py-0.5 ${r.nudge_sent_at ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>{r.nudge_sent_at ? `Reminder ${fmt(r.nudge_sent_at)}` : "Reminder pending"}</span>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
